import { createContext, type CSSProperties, useCallback, useContext, useEffect, useId, useRef, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import type { Currency } from "@cafonapay/shared";
import { CURRENCY_CODES, minorDigits, parseAmount, toMajorString } from "@cafonapay/shared/client";
import { RequestError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Icon } from "./Icon";

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: (props: { id: string; "aria-invalid": boolean; "aria-describedby"?: string }) => ReactNode;
}) {
  const id = useId();
  const { te } = useI18n();
  error = te(error);
  const describedBy = error ? `${id}-err` : hint ? `${id}-hint` : undefined;
  return (
    <div className="field">
      <label htmlFor={id}>
        <span style={{ fontWeight: 600, fontSize: "0.9375rem" }}>{label}</span>
      </label>
      {children({ id, "aria-invalid": Boolean(error), "aria-describedby": describedBy })}
      {error ? (
        <span id={`${id}-err`} className="error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <small id={`${id}-hint`}>{hint}</small>
      ) : null}
    </div>
  );
}

export function TextField({
  label,
  hint,
  error,
  ...input
}: { label: string; hint?: string; error?: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <Field label={label} hint={hint} error={error}>
      {(a11y) => <input className="input" {...a11y} {...input} />}
    </Field>
  );
}

/**
 * Amount entry in major units with a currency picker. Reports the parsed
 * minor-unit value (or null when the text isn't a valid amount).
 */
export function AmountInput({
  label,
  currency,
  onCurrency,
  onAmount,
  error,
  lockCurrency = false,
  autoFocus,
  initialMinor,
}: {
  label: string;
  currency: Currency;
  onCurrency?: (c: Currency) => void;
  onAmount: (minor: number | null) => void;
  error?: string;
  lockCurrency?: boolean;
  autoFocus?: boolean;
  initialMinor?: number;
}) {
  const id = useId();
  const { te, locale } = useI18n();
  const decimals = minorDigits(currency);
  const [text, setText] = useState(initialMinor ? toMajorString(initialMinor, currency).replace(".", locale === "fr" ? "," : ".") : "");
  const [touched, setTouched] = useState(false);
  const parsed = text ? parseAmount(text, currency, locale) : null;
  // One transaction can't exceed 10 million (major units); the server enforces the same cap.
  const tooBig = parsed !== null && parsed > 1_000_000_000;
  useEffect(() => {
    onAmount(tooBig ? null : parsed);
  }, [parsed, tooBig, onAmount]);
  // Say why the amount isn't accepted instead of just disabling the button.
  const ownError = !text || (!touched && !tooBig) ? undefined : tooBig ? "amount_too_big" : parsed === null ? (decimals === 0 && /[.,]\d{1,2}$/.test(text) ? "no_decimals" : "invalid_amount") : undefined;
  const shownError = error ?? ownError;
  return (
    <div className="field">
      <label htmlFor={id}>
        <span style={{ fontWeight: 600, fontSize: "0.9375rem" }}>{label}</span>
      </label>
      <div className="amount-field">
        <input
          id={id}
          inputMode={minorDigits(currency) ? "decimal" : "numeric"}
          autoComplete="off"
          placeholder={decimals ? (locale === "fr" ? "0,00" : "0.00") : "0"}
          value={text}
          autoFocus={autoFocus}
          aria-invalid={Boolean(shownError)}
          onBlur={() => setTouched(true)}
          onChange={(e) => {
            setText(e.target.value.replace(/[^\d.,\s\u00a0\u202f]/g, ""));
          }}
        />
        {lockCurrency ? (
          <span className="chip">{currency}</span>
        ) : (
          <select aria-label="Currency" value={currency} onChange={(e) => onCurrency?.(e.target.value as Currency)}>
            {CURRENCY_CODES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        )}
      </div>
      {shownError && (
        <span className="error" role="alert">
          {te(shownError)}
        </span>
      )}
    </div>
  );
}

export function FormError({ error }: { error: unknown }) {
  const { t, te } = useI18n();
  if (!error) return null;
  const message =
    error instanceof RequestError ? (te(error.code) !== error.code ? te(error.code) : error.message) : error instanceof Error ? error.message : t("errors.generic");
  return (
    <div className="form-error" role="alert">
      {message}
    </div>
  );
}

export const fieldErrors = (error: unknown): Record<string, string> => (error instanceof RequestError ? error.fields : {});

export function Avatar({ name, gold = false }: { name: string; gold?: boolean }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
  return (
    <span className={`avatar${gold ? " avatar-gold" : ""}`} aria-hidden="true">
      {initials || "?"}
    </span>
  );
}

export function PageHeader({ title, back, action }: { title: string; back?: string | true; action?: ReactNode }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  return (
    <header className="spread">
      <div className="row" style={{ "--gap": "4px" } as CSSProperties}>
        {back &&
          (back === true ? (
            <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} aria-label={t("common.back")}>
              <Icon name="back" />
            </button>
          ) : (
            <Link className="btn btn-ghost btn-sm" to={back} aria-label={t("common.back")}>
              <Icon name="back" />
            </Link>
          ))}
        <h1>{title}</h1>
      </div>
      {action}
    </header>
  );
}

export function Loading() {
  const { t } = useI18n();
  return (
    <p className="muted" aria-live="polite" style={{ padding: "24px 0" }}>
      {t("common.loading")}
    </p>
  );
}

export function LoadError({ error, retry }: { error: unknown; retry?: () => void }) {
  const { t } = useI18n();
  return (
    <div className="stack" role="alert">
      <FormError error={error} />
      {retry && (
        <button className="btn btn-sm" onClick={retry} style={{ justifySelf: "start" }}>
          {t("common.retry")}
        </button>
      )}
    </div>
  );
}

/* ---------- Toasts ---------- */

interface Toast {
  id: number;
  text: string;
  tone: "default" | "gold";
}
const ToastCtx = createContext<(text: string, tone?: Toast["tone"]) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const show = useCallback((text: string, tone: Toast["tone"] = "default") => setToast({ id: Date.now(), text, tone }), []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div aria-live="polite">
        {toast && (
          <div key={toast.id} className={`toast${toast.tone === "gold" ? " toast-gold" : ""}`}>
            {toast.text}
          </div>
        )}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

/**
 * A two-step button for money movement and anything that can't be undone: the first tap shows
 * exactly what will happen ("Send 5,25 $ to Kofi B.?"), the second does it.
 */
export function ConfirmButton({
  children,
  prompt,
  confirmLabel,
  onConfirm,
  disabled,
  pending,
  className = "btn btn-primary",
  danger = false,
}: {
  children: ReactNode;
  prompt: ReactNode;
  confirmLabel: ReactNode;
  onConfirm: () => void;
  disabled?: boolean;
  pending?: boolean;
  className?: string;
  danger?: boolean;
}) {
  const { t } = useI18n();
  const [armed, setArmed] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (armed) confirmRef.current?.focus();
  }, [armed]);
  useEffect(() => {
    if (disabled) setArmed(false);
  }, [disabled]);
  if (!armed) {
    return (
      <button type="button" className={className} disabled={disabled || pending} onClick={() => setArmed(true)}>
        {children}
      </button>
    );
  }
  return (
    <div className="confirm-inline" role="group" aria-live="polite">
      <p className="confirm-prompt">{prompt}</p>
      <div className="row">
        <button
          ref={confirmRef}
          type="button"
          className={danger ? "btn btn-danger" : "btn btn-primary"}
          disabled={pending}
          onClick={() => {
            onConfirm();
            setArmed(false);
          }}
        >
          {confirmLabel}
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => setArmed(false)}>
          {t("common.back")}
        </button>
      </div>
    </div>
  );
}
